// import {
//     Content,
//     Icon,
//     Item,
//     ItemText,
//     Root,
//     Trigger,
//     Viewport,
//     // Group,
//     // Label,
//     // Separator,
//     Portal,
//     Value,
// } from '@radix-ui/react-select'

//radix-select doesn't support closing animation
//https://github.com/radix-ui/primitives/issues/1893

import {
  Content,
  DropdownMenuProps,
  Portal,
  Root,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import cl from 'clsx';
import {
  ChangeEventHandler,
  KeyboardEventHandler,
  ReactNode,
  useRef,
  useState,
} from 'react';
import { NavLink, NavLinkProps, useLocation } from 'react-router';

import { PickNonNullable } from '@synapex/tools/typescript';
import * as Contract from '@synapex/shared2/gateway-rest-api';
import CaseBag from '@synapex/frontend-shared/icons/case-bag-18.svg?react';
import CheckIcon from '@synapex/frontend-shared/icons/check-15x12.svg?react';
import ChevronDown from '@synapex/frontend-shared/icons/chevron-down-24.svg?react';
import EditIcon from '@synapex/frontend-shared/icons/edit-16.svg?react';
import PlusIcon from '@synapex/frontend-shared/icons/plus-13.svg?react';

import {
  accountPath,
  isCreatingNewAccount,
  AdvertiserAccount,
  NewAccountChannelRequest,
  newAccountChannelRequestPath,
} from '@/true-fsd/data/advertiser-account';
import { useUpdate } from '@/true-fsd/data/advertiser-account/react-query';
import { useOnClickOutside } from 'usehooks-ts';
import { IconButton } from '@/true-fsd/shared/ui/ButtonLink';
import { preventDefault } from '@/true-fsd/shared/utils/react';

import classes from './AdvertiserAccountSelect.module.scss';

//style and behavior shared by all links
type SelectLinkProps = {
  className: string;
  children: NavLinkProps['children'];
  to: string;
};

const SelectLink = ({ className, to, children }: SelectLinkProps) => {
  const navLinkClassName: NavLinkProps['className'] = ({
    isActive,
    // isPending,
    // isTransitioning,
  }) => {
    return cl(className, {
      [classes.active]: isActive,
    });
    // return cl({
    //     [classes.active]: isActive,
    //     [classes.pending]: isPending,
    //     [classes.transitioning]: isTransitioning,
    // })
  };

  return (
    <NavLink className={navLinkClassName} to={to}>
      {children}
    </NavLink>
  );
};

type RenderAccountProps = {
  account: AdvertiserAccount | NewAccountChannelRequest;
};
const RenderAccount = ({ account }: RenderAccountProps) => {
  const text =
    'name' in account ? account.name : account.new_advertiser_account_name;
  return (
    <>
      <div className={classes.text}>{text}</div>
    </>
  );
};

const makeUpdateDto = (
  name: string,
): Contract.AdvertiserAccount.UpdateInputDto => {
  return {
    name,
  };
};

const NO_ACCOUNT_TEXT = 'Нет аккаунта';

const triggerClassName = (what: 'account' | 'button', className?: string) =>
  cl(classes.item, classes.trigger, classes[what], className);

const linkClassName = (what: 'account' | 'button') =>
  cl(classes.item, classes.link, classes[what]);

type AccountNameInputProps = {
  advertiser_account_id: number;
  initialValue: string;
  onDone: () => void;
};
const AccountNameInput = ({
  onDone,
  initialValue,
  advertiser_account_id,
}: AccountNameInputProps) => {
  const { mutate } = useUpdate({
    advertiser_account_id,
  });
  const [value, setValue] = useState(initialValue);

  const applyChanges = () => {
    if (value !== initialValue) {
      mutate(makeUpdateDto(value));
    }
    onDone();
  };

  const onChange: ChangeEventHandler<HTMLInputElement> = ({
    target: { value },
  }) => setValue(value);

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    switch (e.key) {
      case 'Enter': {
        applyChanges();
        return;
      }
      case 'Escape': {
        onDone();
        return;
      }
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);
  useOnClickOutside(inputRef, applyChanges, 'mouseup');

  return (
    <input
      autoFocus
      ref={inputRef}
      className={cl(classes.item, classes.text, classes.input)}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
    />
  );
};

type RenderNewAccountItemProps = {
  className: string;
  children: ReactNode;
  disabled?: boolean;
};

export type AdvertiserAccountSelectProps = {
  className?: string;
  activeAccount: AdvertiserAccount | null | NewAccountChannelRequest;
  channelRequests: Array<NewAccountChannelRequest>;
  accounts: Array<AdvertiserAccount>;
  newAccount: Contract.AdvertiserAccount.CreateInputDto | null;

  RenderNewAccountItem: (p: RenderNewAccountItemProps) => ReactNode;
} & PickNonNullable<DropdownMenuProps, 'onOpenChange' | 'open'>;
export const AdvertiserAccountSelect = ({
  activeAccount,
  accounts,
  channelRequests,
  newAccount,
  className,
  RenderNewAccountItem,
  open,
  onOpenChange,
}: AdvertiserAccountSelectProps) => {
  const location = useLocation();
  const [editAccountId, setEditAccountId] = useState<number | null>(null);
  const onDoneEdit = () => setEditAccountId(null);

  let trigger: ReactNode;

  if (isCreatingNewAccount(location)) {
    trigger = (
      <Trigger className={triggerClassName('account', className)}>
        <CaseBag className={classes.icon} />
        <div className={classes.text}>
          {newAccount?.name ?? 'Новый аккаунт'}
        </div>
        <div className={classes.spaceHolder} />
        <ChevronDown className={classes.chevron} />
      </Trigger>
    );
  } else if (activeAccount === null) {
    trigger = (
      <Trigger className={triggerClassName('button', className)}>
        <div className={classes.text}>{NO_ACCOUNT_TEXT}</div>
        <div className={classes.spaceHolder} />
        <ChevronDown className={classes.chevron} />
      </Trigger>
    );
  } else {
    trigger = (
      <Trigger className={triggerClassName('account', className)}>
        <CaseBag className={classes.icon} />
        <RenderAccount account={activeAccount} />
        <div className={classes.spaceHolder} />
        <ChevronDown className={classes.chevron} />
      </Trigger>
    );
  }

  return (
    <Root modal open={open} onOpenChange={onOpenChange}>
      {trigger}
      <Portal>
        <Content
          onEscapeKeyDown={editAccountId ? preventDefault : undefined}
          onCloseAutoFocus={preventDefault}
          side={'bottom'}
          align="start"
          className={classes.content}
        >
          <RenderNewAccountItem
            disabled={Boolean(editAccountId)}
            className={linkClassName('button')}
          >
            <div className={classes.text}>Добавить новый аккаунт</div>
            <div className={classes.spaceHolder} />
            <PlusIcon className={classes.icon} />
          </RenderNewAccountItem>
          {accounts.map((account) => {
            if (editAccountId === account.advertiser_account_id) {
              return (
                <AccountNameInput
                  key={account.advertiser_account_id}
                  initialValue={account.name}
                  advertiser_account_id={editAccountId}
                  onDone={onDoneEdit}
                />
              );
            } else {
              const path = accountPath(account);

              return (
                <div
                  key={account.advertiser_account_id}
                  className={classes.accountItemRoot}
                >
                  <SelectLink to={path} className={linkClassName('account')}>
                    <RenderAccount account={account} />
                    <div className={classes.spaceHolder} />
                    <CheckIcon className={classes.check} />
                  </SelectLink>
                  <IconButton
                    Icon={EditIcon}
                    disabled={Boolean(editAccountId)}
                    onClick={() =>
                      setEditAccountId(account.advertiser_account_id)
                    }
                  />
                </div>
              );
            }
          })}
          {channelRequests.map((cr) => {
            const path = newAccountChannelRequestPath(cr);

            return (
              <div
                key={cr.telegram_channel_id}
                className={classes.accountItemRoot}
              >
                <SelectLink to={path} className={linkClassName('account')}>
                  <RenderAccount account={cr} />
                  <div className={classes.spaceHolder} />
                  <CheckIcon className={classes.check} />
                </SelectLink>
              </div>
            );
          })}
          <SelectLink
            to="/workspace?prefix"
            className={linkClassName('button')}
          >
            <div className={classes.text}>{NO_ACCOUNT_TEXT}</div>
            <div className={classes.spaceHolder} />
            <CheckIcon className={classes.check} />
          </SelectLink>
        </Content>
      </Portal>
    </Root>
  );
};
